import { AnyAction, CombinedState, combineReducers } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import { PersistPartial } from 'redux-persist/lib/persistReducer'

import AuthSlice, { authPersistConfig, AuthState } from '../features/azure-b2c/storage'
import CartSlice, { cartPersistConfig, CartState } from '../features/cart/storage/cart.slice'
import OrderSlice, {
  ordersPersistConfig,
  OrdersState,
} from '../features/orders/storage/orders.slice'
import PastProductsSlice, {
  productsPersistConfig,
  ProductsState,
  StocksState,
} from '../features/products/storage'
import SalesSlice, {
  salesPersistConfig,
  SalesState,
} from '../features/sales-agreements/storage/sales.slice'
import StoresSlice, {
  storesPersistConfig,
  StoresState,
} from '../features/stores/storage/stores.slice'
import SettingsSlice, { SettingsState } from '../features/settings/storage/settings.slice'
import DeclarationsSlice, {
  DeclarationsState,
  declarationsPersistConfig,
} from '../features/declarations/declarations.slice'
import { stocksPersistConfig, StocksSlice } from '../features/products/storage/stocks.slice'
import { nutritionPersistConfig, NutritionPromoState } from '../features/nutrition-promo/storage'
import NutritionPromoSlice from '../features/nutrition-promo/storage/nutrition.slice'
import { RemoteConfigState } from '../services/firebase/remote-config/types'
import RemoteConfigSlice, {
  remoteConfigState,
} from '../services/firebase/remote-config/remoteConfig.slice'
import CostPriceSlice, {
  costPricePersistConfig,
  CostPriceState,
} from '../features/cost-price/costprice.slice'
import { SeedCartState, SeedStoresState } from '../features/seed-orders/types/SeedOrderFormType'
import SeedStoresSlice from '../features/seed-orders/storage/seedStores.slice'
import SeedCartSlice from '../features/seed-orders/storage/seedCart.slice'
import QuoteSlice, { QuotesState } from '../features/quotes/storage/quotes.slice'
import { storageDb } from '../services/localforage'

export function clearLocalStorageExceptWhitelist() {
  const whitelist = ['mcid'] // Add more keys here if needed

  // Get all keys from localStorage
  const keys = Object.keys(localStorage)

  // Iterate over all keys
  keys.forEach((key) => {
    // Remove the key if it's not in the whitelist
    if (!whitelist.includes(key)) {
      localStorage.removeItem(key)
    }
  })
}

const reducers = {
  auth: persistReducer(authPersistConfig, AuthSlice.reducer),
  cart: persistReducer(cartPersistConfig, CartSlice.reducer),
  costPrice: persistReducer(costPricePersistConfig, CostPriceSlice.reducer),
  declarations: persistReducer(declarationsPersistConfig, DeclarationsSlice.reducer),
  nutrition: persistReducer(nutritionPersistConfig, NutritionPromoSlice.reducer),
  orders: persistReducer(ordersPersistConfig, OrderSlice.reducer),
  products: persistReducer(productsPersistConfig, PastProductsSlice.reducer),
  quotes: QuoteSlice.reducer,
  remoteConfig: persistReducer(remoteConfigState, RemoteConfigSlice.reducer),
  sales: persistReducer(salesPersistConfig, SalesSlice.reducer),
  seedCart: SeedCartSlice.reducer,
  seedStores: SeedStoresSlice.reducer,
  settings: SettingsSlice.reducer,
  stocks: persistReducer(stocksPersistConfig, StocksSlice.reducer),
  stores: persistReducer(storesPersistConfig, StoresSlice.reducer),
}
const appReducer = combineReducers(reducers)

const rootReducer = (
  // Could replace with 'any' so we don't have to update with new reducer state types
  state:
    | CombinedState<{
        auth: AuthState & PersistPartial
        cart: CartState & PersistPartial
        costPrice: CostPriceState & PersistPartial
        declarations: DeclarationsState & PersistPartial
        nutrition: NutritionPromoState & PersistPartial
        orders: OrdersState & PersistPartial
        products: ProductsState & PersistPartial
        quotes: QuotesState
        remoteConfig: RemoteConfigState & PersistPartial
        sales: SalesState & PersistPartial
        seedCart: SeedCartState
        seedStores: SeedStoresState
        settings: SettingsState
        stocks: StocksState & PersistPartial
        stores: StoresState & PersistPartial
      }>
    | undefined,
  action: AnyAction,
) => {
  if (action.type === 'LOGOUT_REQUEST') {
    storageDb.clear()
    clearLocalStorageExceptWhitelist()
    sessionStorage.clear()
    return appReducer(undefined, action)
  }
  return appReducer(state, action)
}

export type RootState = ReturnType<typeof rootReducer>

export default rootReducer
